import {
    PAGINATION_PAGE_PARAM,
    PAGINATION_PER_PAGE_PARAM,
    SearchParamController,
} from '@clockout-dev/design-elements';
import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import TreasuryDelegate from './treasury.delegate';
import GLAccountController from './controllers/GLAccount.controller';
import TransactionHistoryController from './controllers/TransactionHistory.controller';
import { parsePeriodParams, serializePeriodParams } from '../../utils/periodParams';
import {
    DATE_RANGE_SEARCH_PARAM_KEY,
    TAB_SEARCH_PARAM_KEY,
    TABS,
    TREASURY_DEFAULT_TIMEFRAME,
    TIMEFRAME_SEARCH_PARAM_KEY,
} from './treasury.constants';

/**
 * Page-level orchestrator for the Cashflow Management screen.
 *
 * Follows the same shape as `ConfigurationController`: it instantiates
 * sub-controllers in the constructor (plain JS classes, not React
 * components) and lets each one own its own slice of state. The
 * orchestrator only carries page-level state (auth user, top-level
 * loading flag, active GL tab and the period selector).
 *
 * @extends ClockoutController
 * @extends TreasuryDelegate
 * @extends SearchParamController
 */
class TreasuryController extends Inherit(ClockoutController, TreasuryDelegate, SearchParamController) {
    constructor(props) {
        super(props);
        this.glAccount = new GLAccountController(this);
        this.transactions = new TransactionHistoryController(this);
    }

    /** GL tabs the current session actually holds the scope for. */
    getAccessibleTabs() {
        return TABS.filter((tab) => this.hasScope(tab.requiredScope));
    }

    /**
     * Resolves the active GL tab from the URL. Falls back to the first
     * *accessible* tab when the URL value is missing, unrecognised, or
     * points at a tab the session's scopes don't cover — `Page` filters
     * the same way so the two stay aligned.
     *
     * Returns `null` when the session holds neither GL scope. Defaulting to
     * ADVANCE here would fetch and render the Advance GL account plus its
     * full transaction history for a session scoped to neither.
     */
    resolveInitialGLType() {
        const fromUrl = this.getSearchParamValue(TAB_SEARCH_PARAM_KEY);
        const accessibleTabs = this.getAccessibleTabs();
        if (accessibleTabs.some((t) => t.key === fromUrl)) return fromUrl;
        return accessibleTabs[0]?.key ?? null;
    }

    /**
     * Resolves the active period from the URL, with the date range only
     * coming through when `timeFrame === 'custom'`. Defaults to
     * `DEFAULT_TIMEFRAME` and a null range when either param is missing
     * or invalid. This keeps the URL the source of truth so a deep-link
     * with `?period=ytd` or `?period=custom&range=2026-01-01,2026-03-31`
     * lands on exactly that view.
     *
     * @returns {{ timeFrame: string, dateRange: import('dayjs').Dayjs[] | null }}
     */
    resolveInitialPeriod() {
        return parsePeriodParams(
            this.getSearchParamValue(TIMEFRAME_SEARCH_PARAM_KEY),
            this.getSearchParamValue(DATE_RANGE_SEARCH_PARAM_KEY),
            TREASURY_DEFAULT_TIMEFRAME,
        );
    }

    /**
     * Writes the current period into the URL. `timeFrame !== 'custom'`
     * removes the `range` key so it never lingers from a previous
     * custom selection.
     */
    persistPeriod(timeFrame, dateRange) {
        const { period, range } = serializePeriodParams(timeFrame, dateRange);
        this.setSearchParamValue(TIMEFRAME_SEARCH_PARAM_KEY, period);
        // The shared SearchParamController joins arrays with `,` so the
        // final URL ends up as `range=YYYY-MM-DD,YYYY-MM-DD`.
        this.setSearchParamValue(DATE_RANGE_SEARCH_PARAM_KEY, range);
    }

    async initialize() {
        if (!this.user?.access?.length) {
            this.navigateLogin();
            return;
        }

        const glType = this.resolveInitialGLType();
        const { timeFrame, dateRange } = this.resolveInitialPeriod();
        const { initialPage, initialPerPage } = this.resolveInitialTransactionsPagination();
        this.setState({ user: this.data.user, glType, timeFrame, dateRange });

        // Screen scope but neither GL scope: there is no tab to fetch for,
        // and the view renders the deny state instead.
        if (!glType) {
            this.setLoading(false);
            return;
        }

        // Sub-controllers fetch in parallel — both are independent so
        // there is no reason to serialise them.
        await Promise.all([
            this.glAccount.refresh({ glType, timeFrame, dateRange }),
            this.transactions.refresh({ glType, timeFrame, dateRange, initialPage, initialPerPage }),
        ]);

        this.setLoading(false);
    }

    /**
     * Reads `page` / `perPage` (written by the package Table's
     * `saveSearchParams`) from the URL so a deep-link / refresh lands on
     * the same slice. Falls back to `null` so the sub-controller keeps
     * its own defaults when the URL has nothing useful.
     */
    resolveInitialTransactionsPagination() {
        const rawPage = parseInt(this.getSearchParamValue(PAGINATION_PAGE_PARAM), 10);
        const rawPerPage = parseInt(this.getSearchParamValue(PAGINATION_PER_PAGE_PARAM), 10);
        return {
            initialPage: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : null,
            initialPerPage: Number.isFinite(rawPerPage) && rawPerPage > 0 ? rawPerPage : null,
        };
    }

    /**
     * Snapshot of orchestrator-owned context the GL sub-controller
     * needs to fetch its slices. Centralised so the sub-controller does
     * not have to read directly from `this.state`.
     */
    getGLContext() {
        const { glType, timeFrame, dateRange } = this.state;
        return { glType, timeFrame, dateRange };
    }

    /**
     * Fans a context out to both sub-controllers. Single bail-out point for
     * the no-accessible-tab case, so no GL slice is ever fetched without a
     * tab the session actually holds the scope for.
     */
    refreshGLData(ctx) {
        if (!ctx?.glType) return;
        this.glAccount.refresh(ctx);
        this.transactions.refresh(ctx);
    }

    // ----- Tab + period handlers ---------------------------------------

    /** Forwarded to `<Page onTabChange>`. Resolves to a GL_TYPE key. */
    handleTabChange(glType) {
        if (!this.getAccessibleTabs().some((t) => t.key === glType)) return;

        // Tab switch resets the transactions list to page 1; drop the
        // stale `page` from the URL so a refresh does not restore a
        // page that no longer exists for the new dataset.
        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);
        this.setState({ glType }, () => {
            this.refreshGLData(this.getGLContext());
        });
    }

    /** `DashboardPeriodControls.onTimeFrameChange`. Persists to URL. */
    handleTimeFrameChange(timeFrame) {
        const nextDateRange = timeFrame === 'custom' ? this.state.dateRange : null;
        this.persistPeriod(timeFrame, nextDateRange);
        this.setState({ timeFrame, dateRange: nextDateRange }, () => {
            // Custom range needs both ends before we can fetch; bail
            // until the user picks a range in the picker.
            if (timeFrame === 'custom' && (!nextDateRange?.[0] || !nextDateRange?.[1])) return;
            this.refreshGLData(this.getGLContext());
        });
    }

    /** `DashboardPeriodControls.onDateRangeChange`. Custom-range only. */
    handleDateRangeChange(dates) {
        if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
            // Range cleared while staying in `custom` — drop the URL
            // entry so a refresh does not restore stale dates.
            this.persistPeriod('custom', null);
            this.setState({ dateRange: null });
            return;
        }
        this.persistPeriod('custom', dates);
        this.setState({ timeFrame: 'custom', dateRange: dates }, () => {
            this.refreshGLData(this.getGLContext());
        });
    }
}

export default TreasuryController;
